import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Landmark, ChevronRight, Clock } from 'lucide-react';
import { blogPosts } from '../data/blogData';
import { govtGuides } from '../data/govtGuidesData';

const ResourcesPreview = () => {
  const topBlogs = blogPosts.slice(0, 3);
  const topGuides = govtGuides.slice(0, 3);

  return (
    <section className="py-16 sm:py-20 bg-gray-50 border-y border-gray-200" data-testid="resources-preview">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Blog preview */}
        <div className="mb-16">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-3">
                <BookOpen size={14} /> Blog &amp; Resources
              </div>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
                Latest from our editorial team
              </h2>
              <p className="mt-2 text-base text-gray-600 max-w-2xl">
                Detailed guides on medical certificate rules, telemedicine, sick leave and Indian healthcare law.
              </p>
            </div>
            <Link to="/blog" data-testid="home-view-all-blogs" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800 transition">
              View all articles <ChevronRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {topBlogs.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`home-blog-card-${p.slug}`}
                className="group bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-md transition"
              >
                <div className="text-xs font-medium text-blue-700 mb-2">{p.category}</div>
                <h3 className="font-semibold text-gray-900 text-base leading-snug group-hover:text-blue-700 transition-colors">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm text-gray-600 line-clamp-2">{p.excerpt}</p>
                <div className="mt-4 flex items-center gap-1.5 text-xs text-gray-500">
                  <Clock size={12} /> {p.readTime}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Govt Guides preview */}
        <div>
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-medium mb-3">
                <Landmark size={14} /> Government Guides
              </div>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
                Navigate Indian government documents with confidence
              </h2>
              <p className="mt-2 text-base text-gray-600 max-w-2xl">
                Plain-English walkthroughs for CGHS, ESIC, PM-JAY, EWS, OBC NCL, CCS Leave Rules, Aadhaar and more.
              </p>
            </div>
            <Link to="/govt-guides" data-testid="home-view-all-guides" className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange-700 hover:text-orange-800 transition">
              View all guides <ChevronRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {topGuides.map((g) => (
              <Link
                key={g.slug}
                to={`/govt-guides/${g.slug}`}
                data-testid={`home-guide-card-${g.slug}`}
                className="group bg-white border border-gray-200 rounded-2xl p-6 hover:border-orange-400 hover:shadow-md transition"
              >
                <div className="text-xs font-medium text-orange-700 mb-2">{g.category}</div>
                <h3 className="font-semibold text-gray-900 text-base leading-snug group-hover:text-orange-700 transition-colors">
                  {g.title}
                </h3>
                <p className="mt-3 text-sm text-gray-600 line-clamp-2">{g.summary}</p>
                <div className="mt-4 flex items-center gap-1.5 text-xs text-gray-500">
                  <Clock size={12} /> {g.readTime}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourcesPreview;
